"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="124359" className="flex gap-4">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative bg-transparent px-1 py-2 font-sans text-sm font-semibold text-[#3b456e] transition-colors hover:bg-transparent hover:text-[#51b7f5] focus:bg-transparent focus:text-[#51b7f5] data-[active=true]:bg-transparent data-[active=true]:text-[#51b7f5]">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#51b7f5] transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-4 p-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block font-sans text-2xl font-semibold text-[#3b456e] transition-colors hover:text-[#51b7f5]" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <>
      <header id="header" className={`sticky top-0 z-40 w-full transition-all duration-300 ${isScrolled ? "bg-white/80 border-b border-slate-200 backdrop-blur-lg py-3 shadow-md" : "bg-white border-b border-transparent py-5"}`}>
        <div className="container mx-auto flex items-center justify-between px-4 md:px-6">
          
          <Link to="/" className="flex items-center z-50">
            <img src="https://ruling-egret.10web.cloud/wp-content/uploads/2026/04/Physically-Sound-PT-Logo-icon-3.png" alt="Physically Sound" className="h-10 w-auto object-contain" />
          </Link>

          
          <div className="hidden md:flex md:flex-1 md:justify-center">
            <MenuProvider menu_id="42">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          
          <div className="flex items-center gap-4 z-50">
            <Button asChild className="hidden md:inline-flex bg-[#51b7f5] text-white hover:bg-[#51b7f5]/90 font-bold shadow-md tracking-tight">
              <Link to="/ContactPage">Book Your Evaluation</Link>
            </Button>

            <Button variant="ghost" size="icon" className="md:hidden text-[#3b456e] hover:text-[#51b7f5] hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </header>

      
      {isMobileMenuOpen && <div className="fixed inset-0 z-30 bg-white/95 backdrop-blur-lg md:hidden pt-24 pb-6 flex flex-col justify-between animate-in fade-in duration-200">
          <div className="overflow-y-auto">
            <MenuProvider menu_id="42">
              <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
          <div className="p-6 border-t border-slate-100 mt-auto">
            <Button asChild className="w-full bg-[#51b7f5] text-white hover:bg-[#51b7f5]/90 font-bold shadow-md py-6 text-lg tracking-tight" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/ContactPage">Book Your Evaluation</Link>
            </Button>
          </div>
        </div>}
    </>;
}